import { SetStateAction, Dispatch, useEffect, useState } from "react";
import styled from "styled-components";
import { KeyValueObj } from "@/common/types";
import Input from "@/common/components/Input/Input";
import { states } from "@/common/data";
import { AddressManager } from "@/common/components/addresses";
import { MultiSelect } from "@/common/components/multi-selectInput";
import { formatQuantity, readBatchUnit } from "@/common/utils/quantity";
import { capitalize, pluralize } from "@/common/utils/general";

export interface IReqFormProps {
  product: KeyValueObj;
  orders: KeyValueObj;
  setOrders: Dispatch<SetStateAction<KeyValueObj>>;
  type?: string;
}

interface ReqProps {
  isPreview: boolean;
}

const ReqFormWrapper = styled.div<ReqProps>`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-around;
  background-color: ${({ theme }) => theme.bg.prim_500};
  padding: 2rem 0;

  .forms-wrapper {
    width: 90%;
    gap: 2rem;
    padding: 1rem 0;

    @media (max-width: ${({ theme }) => theme.screen.mobile}) {
      width: 100%;
      padding: 0;
    }

    .section-header {
      width: 100%;
      font-size: 0.95rem;
      font-weight: 600;
      color: ${({ theme }) => theme.text.primary};
      padding: 0 0.85rem 0.5rem;
      border-bottom: 1px solid ${({ theme }) => theme.border.light};
      margin-bottom: 0.5rem;
    }

    .info,
    .inputs {
      width: 100%;
      flex-wrap: wrap;
      justify-content: flex-start;
      align-items: flex-start;
      gap: 1.5rem 1.1rem;
      padding: 0.85rem;

      .info-wrapper,
      .input-wrapper {
        width: 23%;
        align-items: flex-start;
        gap: 0.5rem;

        @media (max-width: ${({ theme }) => theme.screen.mobile}) {
          width: 46%;
        }

        h3 {
          font-weight: 500;
          font-size: 0.85rem;
          color: ${({ theme }) => theme.text.primary};
        }

        .title {
          color: ${({ theme }) => theme.text.prim_100};
          font-weight: 400;
        }
      }
    }

    .line {
      width: 90%;
      height: 1px;
      background-color: ${({ theme }) => theme.border.light};
    }

    .inputs {
      justify-content: flex-start;

      .input-wrapper {
        width: ${({ isPreview }) => (isPreview ? "22%" : "47%")};
        gap: 0.5rem;
        position: relative;

        @media (max-width: ${({ theme }) => theme.screen.mobile}) {
          width: 46%;
        }

        .input-error-message {
          color: red;
          font-size: 0.8rem;
        }

        input,
        textarea,
        select {
          width: 100%;
          padding: 0.65rem;
          outline: none;
          border: 1px solid ${({ theme }) => theme.border.input};
          border-radius: 0.25rem;
          resize: none;
          color: ${({ theme }) => theme.text_prim_100};
          font-weight: 400;
          font-size: 0.8rem;
        }
      }

      .buyer-note {
        flex-basis: 100%;
        align-self: stretch;
      }
    }
  }
`;

export function ReqForm({ product, orders, setOrders, type }: IReqFormProps) {
  //STATES
  const [formValues, setFormValues] = useState({});
  const [selectedProducts, setSelectedProducts] = useState<
    {
      id: string;
      title: string;
      amount: number;
      /** Whole selling-level packages in stock — the order ceiling for this batch. */
      quantity: number;
      /** MED-697: the same stock read in packaging levels, for display only. */
      quantityLabel: string;
      /** MED-706: base units one selling-level package of this batch holds. */
      multiplier: number;
      /** MED-706: this batch's own selling-level label, e.g. "Carton" / "Pack". */
      unitLabel: string;
      sellingPrice: string;
      discountPercentage: number | null;
      originalPrice: number | null;
    }[]
  >([]);
  const isPreview = type === "preview";
  const id = product?.id;

  //USEEFFECTS
  useEffect(() => {
    if (isPreview) return;
    setOrders((prev: KeyValueObj) => ({
      ...prev,
      [product.id]: {
        ...prev[product.id],
        selected_batches: selectedProducts,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProducts]);

  //initialize the values
  useEffect(() => {
    if (isPreview) return;
    setOrders((prev: any) => {
      return {
        ...prev,
        [product?.id]: {
          ...prev[product?.id],
          product_id: product?.id,
          supplier_id: prev[product?.id]?.supplier_id ?? "",
          package_type: prev[product?.id]?.package_type ?? "",
        },
      };
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Packaging hierarchy is returned ordered outermost -> base unit. Each layer's
  // containment_quantity says how many of the next (child) layer it holds; the
  // base unit layer holds nothing (containment_quantity === null). Replaces the
  // retired primary/secondary/tertiary packaging columns — see PackagingLayer
  // on the backend. Same pattern as products/subs/product-details/index.tsx.
  const packagingLayers: KeyValueObj[] = Array.isArray(product?.packaging_layers)
    ? product.packaging_layers
    : [];

  function layerValue(layer: KeyValueObj, index: number) {
    const quantity = layer?.containment_quantity;
    if (quantity === null || quantity === undefined)
      return capitalize(layer?.packaging_type);
    const childType = packagingLayers[index + 1]?.packaging_type;
    const contains = pluralize(quantity, capitalize(childType) || "unit");
    return `${capitalize(layer?.packaging_type)} (contains ${contains})`;
  }

  // MED-706: stock and price are shown/collected in each BATCH's own selling
  // level, not the product's outermost packaging layer. A manufacturer is
  // locked to that layer so the two agreed, but an importer or distributor
  // sells below it and one product's batches can sit at different levels.
  // `readBatchUnit` reads that level from the batch's own `quantity_display`
  // (MED-697); buildOrderLine converts back to base units per batch at the API
  // boundary, once.

  // `selling_price_per_unit` is already discount-applied (see DrugSerializer#
  // batches -> PricingManager::UnitConverter.apply_discount on the backend).
  // `originalPrice` is derived back out for display only, so a discounted
  // batch can show the struck-through pre-discount price alongside it.
  const batchOptions = (product.batches ?? []).map((batch: any) => {
    const unit = readBatchUnit(batch.quantity_display);
    const sellingPrice = Number(batch.selling_price_per_unit) * unit.multiplier;
    const discountPercentage = batch.discount_percentage ?? null;
    const originalPrice = discountPercentage
      ? sellingPrice / (1 - discountPercentage / 100)
      : null;

    return {
      id: batch.inventory_id,
      // MultiSelect renders `title` as plain text in both the dropdown list
      // and the selected chips (no badge/subtitle support, and it's a shared
      // component used across the app) — bake the discount in here rather
      // than touching MultiSelect itself.
      title: discountPercentage
        ? `${batch.batch_number} (${discountPercentage}% OFF)`
        : batch.batch_number,
      quantity: unit.inStock,
      // MED-697: what the buyer READS for this batch - the server's own
      // reading of the stock, which keeps the remainder below the selling
      // level ("3 Cartons + 2 Packs"). `quantity` above is the ORDER
      // CEILING and stays floored to whole sellable containers, so the two
      // are deliberately different numbers.
      quantityLabel: formatQuantity(
        batch.quantity_display,
        batch.quantity_in_stock,
      ),
      multiplier: unit.multiplier,
      unitLabel: unit.label,
      sellingPrice,
      discountPercentage,
      originalPrice,
    };
  });

  return (
    <ReqFormWrapper isPreview={isPreview}>
      <div className="forms-wrapper flex-col">
        {isPreview && <h3 className="section-header">Product Details</h3>}
        <div className="info flex-row">
          <div className="info-wrapper flex-col">
            <h3 className="title">Name</h3>
            <h3 className="value">{product?.atc_code?.code_title}</h3>
          </div>

          <div className="info-wrapper flex-col">
            <h3 className="title">ATC Code</h3>
            <h3 className="value">{product?.atc_code?.code_identifier}</h3>
          </div>

          <div className="info-wrapper flex-col">
            <h3 className="title">Medex Code</h3>
            <h3 className="value">{product?.medex_code}</h3>
          </div>

          <div className="info-wrapper flex-col">
            <h3 className="title">Brand</h3>
            <h3 className="value">{product?.brand_name}</h3>
          </div>

          <div className="info-wrapper flex-col">
            <h3 className="title">Product strength</h3>
            <h3 className="value">
              {product?.strength + product?.strength_unit}
            </h3>
          </div>

          <div className="info-wrapper flex-col">
            <h3 className="title">Form</h3>
            <h3 className="value">{product?.form}</h3>
          </div>

          <div className="info-wrapper flex-col">
            <h3 className="title">Classification</h3>
            <h3 className="value">{product?.classification}</h3>
          </div>

          <div className="info-wrapper flex-col">
            <h3 className="title">Regulation</h3>
            <h3 className="value">{product?.regulation}</h3>
          </div>

          {packagingLayers.map((layer: KeyValueObj, index: number) => (
            <div className="info-wrapper flex-col" key={layer?.id ?? index}>
              <h3 className="title">
                Level {layer?.position ?? index + 1} Packaging
              </h3>
              <h3 className="value">{layerValue(layer, index)}</h3>
            </div>
          ))}
        </div>

        <div className="line"></div>
        {isPreview && <h3 className="section-header">Selected Batches</h3>}
        <div className="inputs flex-row">
          {!isPreview ? (
            <>
              <MultiSelect
                fullWidth
                name="products"
                prompt="Select Products batches"
                placeholder="Choose batches"
                options={batchOptions}
                returnId
                formValues={formValues}
                setFormValues={setFormValues}
                onEveryChange={(selectedIds) => {
                  setSelectedProducts((prev) => {
                    const existingAmounts = Object.fromEntries(
                      prev.map((item) => [item.id, item.amount]),
                    );

                    return batchOptions
                      .filter((batch: any) => selectedIds.includes(batch.id))
                      .map((batch: any) => ({
                        id: batch.id,
                        title: batch.title,
                        amount: existingAmounts[batch.id] || 0,
                        quantity: batch.quantity,
                        quantityLabel: batch.quantityLabel,
                        multiplier: batch.multiplier,
                        unitLabel: batch.unitLabel,
                        sellingPrice: batch.sellingPrice,
                        discountPercentage: batch.discountPercentage,
                        originalPrice: batch.originalPrice,
                      }));
                  });
                }}
              />

              <div className="mt-4 space-y-3 w-full">
                {selectedProducts.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-4 border rounded p-3"
                  >
                    <div className="flex flex-col flex-1 gap-1">
                      <span className="font-medium text-sm">{item.title}</span>
                      <span className="text-xs text-gray-500">
                        In stock: {item.quantityLabel} · Price:{" "}
                        {item.discountPercentage ? (
                          <>
                            <span className="line-through text-gray-400">
                              ₦{Number(item.originalPrice).toLocaleString()}
                            </span>{" "}
                            <span className="font-medium text-green-700">
                              ₦{Number(item.sellingPrice).toLocaleString()}
                            </span>
                          </>
                        ) : (
                          <>₦{Number(item.sellingPrice).toLocaleString()}</>
                        )}{" "}
                        / {item.unitLabel}
                      </span>
                      {item.amount > item.quantity && (
                        <span className="text-xs text-red-600">
                          Only {pluralize(item.quantity, item.unitLabel)}{" "}
                          available in this batch
                        </span>
                      )}
                    </div>
                    <input
                      type="number"
                      min={0}
                      max={item.quantity}
                      placeholder="Qty"
                      onChange={(e) => {
                        const amount = Number(e.target.value);
                        setSelectedProducts((prev) =>
                          prev.map((p) =>
                            p.id === item.id ? { ...p, amount } : p,
                          ),
                        );
                      }}
                      className={`border rounded px-3 py-2 w-24 ${
                        item.amount > item.quantity
                          ? "border-red-500 focus:outline-red-500"
                          : ""
                      }`}
                    />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="input-wrapper flex-col" style={{ width: "100%" }}>
              {(orders[id]?.selected_batches ?? []).length === 0 ? (
                <h3 className="value">No batches selected</h3>
              ) : (
                <div className="mt-2 space-y-2 w-full">
                  {(
                    orders[id]?.selected_batches as {
                      id: string;
                      title: string;
                      amount: number;
                      quantity: number;
                      quantityLabel: string;
                      multiplier: number;
                      unitLabel: string;
                      sellingPrice: string;
                      discountPercentage: number | null;
                      originalPrice: number | null;
                    }[]
                  ).map((batch) => (
                    <div
                      key={batch.id}
                      className="flex justify-between items-center w-full border rounded p-2"
                    >
                      <span className="font-medium text-sm">{batch.title}</span>
                      <span className="text-xs text-gray-500">
                        Qty ordered: {pluralize(batch.amount, batch.unitLabel)}{" "}
                        ·{" "}
                        {batch.discountPercentage ? (
                          <>
                            <span className="line-through text-gray-400">
                              ₦{Number(batch.originalPrice).toLocaleString()}
                            </span>{" "}
                            <span className="font-medium text-green-700">
                              ₦{Number(batch.sellingPrice).toLocaleString()}
                            </span>
                          </>
                        ) : (
                          <>₦{Number(batch.sellingPrice).toLocaleString()}</>
                        )}{" "}
                        / {batch.unitLabel}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </ReqFormWrapper>
  );
}

export interface IOrderDeliveryDetailsProps {
  isPreview?: boolean;
  deliveryDetails: KeyValueObj;
  setDeliveryDetails: Dispatch<SetStateAction<KeyValueObj>>;
}

export function OrderDeliveryDetails({
  isPreview,
  deliveryDetails,
  setDeliveryDetails,
}: IOrderDeliveryDetailsProps) {
  const [selectedAddress, setSelectedAddress] = useState<KeyValueObj>({});

  useEffect(() => {
    setDeliveryDetails((prev: any) => ({ ...prev, ...selectedAddress }));

    // eslint-disable-next-line
  }, [selectedAddress]);

  function updateData(name: string, value: any) {
    setDeliveryDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  return (
    <ReqFormWrapper isPreview={!!isPreview}>
      <div className="forms-wrapper flex-col">
        {isPreview && <h3 className="section-header">Delivery Details</h3>}
        <div className="inputs flex-row">
          {!isPreview ? (
            <>
              <AddressManager
                selectedAddress={selectedAddress}
                setSelectedAddress={setSelectedAddress}
                hideDelete
              />
              <>
                <Input
                  placeholder={"Enter order note"}
                  prompt={"Order Note"}
                  fullWidth={false}
                  shortWidth={false}
                  name={"buyer_note"}
                  inputType="textarea"
                  indexLevel={5}
                  updateData={updateData}
                  options={states}
                  updateValidity={(_name: string, _validity: boolean) => {}}
                  formValues={deliveryDetails}
                  setFormValues={setDeliveryDetails}
                />

                <Input
                  placeholder={"Enter delivery date"}
                  prompt={"Expected Delivery Date"}
                  fullWidth={false}
                  shortWidth={false}
                  name={"expected_delivery_date"}
                  inputType="date"
                  indexLevel={6}
                  updateData={updateData}
                  options={states}
                  updateValidity={(_name: string, _validity: boolean) => {}}
                  formValues={deliveryDetails}
                  setFormValues={setDeliveryDetails}
                  rules={{
                    notInTheFuture: {
                      expectedValue: false,
                      errorMessage:
                        "Expected delivery date cannot be in the past",
                    },
                  }}
                />
              </>
            </>
          ) : null}

          {isPreview ? (
            <>
              <div className="input-wrapper  flex-col">
                <h3 className="title">Delivery State</h3>
                <h3 className="preview">{deliveryDetails?.state}</h3>
              </div>
              <div className="input-wrapper flex-col">
                <h3 className="title">Delivery Lga</h3>
                <h3 className="preview">{deliveryDetails?.lga}</h3>
              </div>
              <div className="input-wrapper flex-col">
                <h3 className="title">Delivery City</h3>
                <h3 className="preview">{deliveryDetails?.city}</h3>
              </div>
              <div className="input-wrapper  flex-col">
                <h3 className="title">Address Line one</h3>
                <h3 className="preview">{deliveryDetails?.address_line_one}</h3>
              </div>
              <div className="input-wrapper flex-col">
                <h3 className="title">Address Line two</h3>
                <h3 className="preview">{deliveryDetails?.address_line_two}</h3>
              </div>
              <div className="input-wrapper  flex-col">
                <h3 className="title">Postal Code</h3>
                <h3 className="preview">{deliveryDetails?.postal_code}</h3>
              </div>

              <div className="input-wrapper flex-col">
                <h3 className="title"> Expected Delivery Date</h3>
                <h3 className="preview">
                  {deliveryDetails?.expected_delivery_date}
                </h3>
              </div>
              <div className="input-wrapper flex-col">
                <h3 className="title">Buyer Note</h3>
                <h3 className="preview">{deliveryDetails?.buyer_note}</h3>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </ReqFormWrapper>
  );
}
